import React from 'react'
import styled from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { avatarUrl } from 'utils/avatarUrl'

export const Card = styled(Flex)`
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  padding: 16px;
  gap: 12px;
`

export const Row = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
  &:last-child {
    border-bottom: none;
  }
`

export const Rank = styled(Text)`
  width: 32px;
  flex-shrink: 0;
  opacity: 0.6;
`

export const Avatar = styled.div<{ $src?: string | null }>`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme, $src }) =>
    $src ? `url(${$src}) center/cover no-repeat` : theme.colors.gray9};
`

export const Grow = styled(Flex)`
  flex: 1;
  min-width: 0;
  flex-direction: column;
`

export const Muted = styled(Text)`
  opacity: 0.6;
`

/** Wallets are long and every row shows one. */
export function shortWallet(wallet: string): string {
  return `${wallet.slice(0, 6)}…${wallet.slice(-4)}`
}

/**
 * A person, or an address.
 *
 * This is the privacy switch made visible: somebody who opted in is shown as
 * themselves, and everybody else is shown as the wallet they are. Never invent
 * a name for an address that has not claimed one.
 */
export function Identity({
  wallet,
  name,
  tag,
  avatarPath
}: {
  wallet: string
  name?: string | null
  tag?: string | null
  avatarPath?: string | null
}) {
  return (
    <>
      <Avatar $src={avatarUrl(avatarPath)} />
      <Grow>
        <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>{name ?? shortWallet(wallet)}</Text>
        {tag ? <Muted variant={TextVariant.BODY_SMALL}>@{tag}</Muted> : null}
      </Grow>
    </>
  )
}

/**
 * The three states a table can be in, and they are three, not two.
 *
 * "Nothing here yet" and "we could not find out" are different facts. A table
 * that renders a failed fetch as an empty list tells the reader something
 * untrue and gives them nothing to do about it — the mistake this platform has
 * now made with a balance, a leaderboard and a voting power.
 */
export function TableState({
  loading,
  available,
  empty,
  emptyText,
  unavailableText
}: {
  loading: boolean
  available: boolean
  empty: boolean
  emptyText: string
  unavailableText: string
}) {
  if (loading) return <Muted variant={TextVariant.BODY_SMALL}>Loading…</Muted>
  if (!available) return <Muted variant={TextVariant.BODY_SMALL}>{unavailableText}</Muted>
  if (empty) return <Muted variant={TextVariant.BODY_SMALL}>{emptyText}</Muted>
  return null
}

/** A number the chain did not give us is a dash, never a zero. */
export function PowerValue({ value }: { value: number | null }) {
  return (
    <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
      {value === null ? '—' : value.toLocaleString()}
    </Text>
  )
}
